// src/pages/leads/useEditLeadPage.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  useLead,
  useUpdateLeadMutation,
  useTransferLeadProductMutation,
} from "@/api/features/leads";
import type {
  LeadWithProducts,
  UpdateLeadBody,
  TransferLeadProductBody,
  LeadProductVisaDetail,
  UpsertLeadProductVisaDetailBody,
} from "@/api/features/leads";

import {
  useProductsList,
  usePublicProductsList,
} from "@/api/features/products/queries";
import type {
  PaginatedProductItems,
  Product,
} from "@/api/features/products/types";

import { useUsers } from "@/api/features/users/queries";
import type {
  PaginatedUsers,
  UserResult,
} from "@/api/features/users/types";
import { getAuth } from "@/api/client";

/* ============================================
 * UI enums / types
 * ============================================ */

export type LeadStatusUi = "Pending" | "Completed" | "Cancelled";
export type LeadSourceUi =
  | "Walk-in"
  | "Referral"
  | "Online"
  | "Advertisement"
  | "Social Media"
  | "Partner"
  | "Phone Inquiry"
  | "Email Campaign"
  | "Other";
export type PriorityUi = "Low" | "Medium" | "High" | "Urgent";

export type EditLeadFormState = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  date: string; // yyyy-mm-dd (leadDate)

  // Lead-level primary assignee
  assignedToUserId: number | null;

  status: LeadStatusUi;
  source?: LeadSourceUi;
  priority?: PriorityUi;
  notes?: string;

  /**
   * Multi-product selection:
   * - Same vibe as create: one lead can have multiple products.
   * - We keep this as productIds[]
   */
  products: number[];
  productsOpen: boolean;

  /**
   * Legacy single productId (no longer used in UI, but kept for
   * compatibility if anything still expects it).
   */
  productId: number | null;
};

export type TransferTimelineEntry = {
  id: string;
  when: string;

  fromProductName: string;
  toProductName: string;

  fromUserName: string;
  toUserName: string;

  reason: string;
  actor: string;
};

export type VisaDetailsDraftState = {
  countryIso2: string;
  enquiryAt: string;
  docsReceivedAt: string;
  docsSubmittedAt: string;
  docsCollectedAt: string;
  appointmentAt: string;
  invoiceAmount: string;
  receiptAmount: string;
  loginId: string;
  loginPassword: string;
  remark: string;
};

/* ============================================
 * Enum mappers (API ↔ UI)
 * ============================================ */

function formatDateYmd(input: unknown): string {
  if (!input) return "";
  const d =
    input instanceof Date ? input : new Date(input as string | number);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function mapStatusToUi(
  status: UpdateLeadBody["status"] | null | undefined
): LeadStatusUi {
  switch (status) {
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    case "PENDING":
    default:
      return "Pending";
  }
}

function mapStatusToApi(
  status: LeadStatusUi | undefined
): UpdateLeadBody["status"] {
  switch (status) {
    case "Completed":
      return "COMPLETED";
    case "Cancelled":
      return "CANCELLED";
    case "Pending":
    default:
      return "PENDING";
  }
}

function mapSourceToUi(
  source: UpdateLeadBody["source"] | null | undefined
): LeadSourceUi | undefined {
  switch (source) {
    case "WALK_IN":
      return "Walk-in";
    case "REFERRAL":
      return "Referral";
    case "ONLINE":
      return "Online";
    case "ADVERTISEMENT":
      return "Advertisement";
    case "SOCIAL_MEDIA":
      return "Social Media";
    case "PARTNER":
      return "Partner";
    case "PHONE_INQUIRY":
      return "Phone Inquiry";
    case "EMAIL_CAMPAIGN":
      return "Email Campaign";
    case "OTHER":
      return "Other";
    default:
      return undefined;
  }
}

function mapSourceToApi(
  source: LeadSourceUi | undefined
): UpdateLeadBody["source"] {
  switch (source) {
    case "Walk-in":
      return "WALK_IN";
    case "Referral":
      return "REFERRAL";
    case "Online":
      return "ONLINE";
    case "Advertisement":
      return "ADVERTISEMENT";
    case "Social Media":
      return "SOCIAL_MEDIA";
    case "Partner":
      return "PARTNER";
    case "Phone Inquiry":
      return "PHONE_INQUIRY";
    case "Email Campaign":
      return "EMAIL_CAMPAIGN";
    case "Other":
      return "OTHER";
    default:
      return undefined;
  }
}

function mapPriorityToUi(
  priority: UpdateLeadBody["priority"] | null | undefined
): PriorityUi | undefined {
  switch (priority) {
    case "LOW":
      return "Low";
    case "MEDIUM":
      return "Medium";
    case "HIGH":
      return "High";
    case "URGENT":
      return "Urgent";
    default:
      return undefined;
  }
}

function mapPriorityToApi(
  priority: PriorityUi | undefined
): UpdateLeadBody["priority"] {
  switch (priority) {
    case "Low":
      return "LOW";
    case "Medium":
      return "MEDIUM";
    case "High":
      return "HIGH";
    case "Urgent":
      return "URGENT";
    default:
      return undefined;
  }
}

function isVisaSlug(value: unknown): boolean {
  const slug = String(value ?? "").trim().toLowerCase();
  return slug === "visa" || slug === "visas";
}

/* ============================================
 * Transfer reasons (UI options)
 * ============================================ */

export const TRANSFER_REASON_OPTIONS = [
  { label: "Late response", value: "LATE_RESPONSE" },
  { label: "Higher price", value: "HIGHER_PRICE" },
  { label: "Subject Knowledge", value: "SUBJECT_KNOWLEDGE" },
  { label: "Behaviour", value: "BEHAVIOUR" },
  { label: "Trip cancelled", value: "TRIP_CANCELLED" },
  // "OTHERS" is frontend-only; backend LeadReasonEnum currently does NOT have it
  { label: "Others", value: "OTHERS" },
] as const;

// Optional: for mapping enum codes to human labels (display only)
const TRANSFER_REASON_CODE_TO_LABEL: Record<string, string> = {
  LATE_RESPONSE: "Late response",
  HIGHER_PRICE: "Higher price",
  SUBJECT_KNOWLEDGE: "Subject Knowledge",
  BEHAVIOUR: "Behaviour",
  TRIP_CANCELLED: "Trip cancelled",
  OTHERS: "Others",
};

function formatDateInput(input: unknown): string {
  if (!input) return "";
  const raw = String(input);
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = input instanceof Date ? input : new Date(input as string | number);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatAmountInput(input: unknown): string {
  if (input === null || input === undefined || input === "") return "";
  const n = typeof input === "number" ? input : Number(input);
  if (!Number.isFinite(n)) return "";
  return String(n);
}

/* ============================================
 * Hook
 * ============================================ */

export function useEditLeadPage() {
  const authUser = getAuth().user as any;
  const isAffiliate = String(authUser?.roleName ?? "").trim() === "Affiliate Partner";
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const numericId = id ? Number(id) : NaN;
  const invalidId = !id || Number.isNaN(numericId);

  const {
    data: lead,
    isLoading,
    isError,
    refetch: refetchLead,
  } = useLead(invalidId ? null : numericId);

  const updateMutation = useUpdateLeadMutation();
  const transferMutation = useTransferLeadProductMutation(
    invalidId ? 0 : numericId
  );

  // Products + users for dropdowns
  const adminProductsQuery = useProductsList(
    { page: 1, limit: 200 },
    { enabled: !isAffiliate }
  );
  const affiliateProductsQuery = usePublicProductsList(
    { page: 1, limit: 200 },
    { enabled: isAffiliate }
  );
  const productsData = isAffiliate
    ? affiliateProductsQuery.data
    : adminProductsQuery.data;

  const { data: usersData } = useUsers(
    {
      page: 1,
      limit: 200,
    },
    { enabled: !isAffiliate }
  );

  const productOptions = useMemo(
    () =>
      ((productsData as PaginatedProductItems | undefined)?.items ?? []).map(
        (p: Product) => ({
          value: String(p.id),
          label: p.name,
        })
      ),
    [productsData]
  );

  const employeeOptions = useMemo(() => {
    if (isAffiliate && Number.isFinite(Number(authUser?.id)) && Number(authUser.id) > 0) {
      const name = `${authUser?.firstName ?? ""} ${authUser?.lastName ?? ""}`.trim();
      const label = name || authUser?.email || authUser?.username || `User #${authUser.id}`;
      return [{ value: String(authUser.id), label }];
    }

    return ((usersData as PaginatedUsers | undefined)?.items ?? []).map(
      (u: UserResult) => {
        const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
        const label = name || u.email || u.username || `User #${u.id}`;
        return {
          value: String(u.id),
          label,
        };
      }
    );
  }, [authUser?.email, authUser?.firstName, authUser?.id, authUser?.lastName, authUser?.username, isAffiliate, usersData]);

  // Maps for id → display name (for timeline)
  const productNameById = useMemo(() => {
    const map = new Map<number, string>();
    const items = (productsData as PaginatedProductItems | undefined)?.items;
    (items ?? []).forEach((p: Product) => {
      map.set(p.id, p.name);
    });
    return map;
  }, [productsData]);

  const productSlugById = useMemo(() => {
    const map = new Map<number, string>();
    const items = (productsData as PaginatedProductItems | undefined)?.items;
    (items ?? []).forEach((p: Product) => {
      map.set(p.id, p.slug);
    });
    return map;
  }, [productsData]);

  const userNameById = useMemo(() => {
    const map = new Map<number, string>();
    if (isAffiliate && Number.isFinite(Number(authUser?.id)) && Number(authUser.id) > 0) {
      const name = `${authUser?.firstName ?? ""} ${authUser?.lastName ?? ""}`.trim();
      map.set(Number(authUser.id), name || authUser?.email || authUser?.username || `User #${authUser.id}`);
      return map;
    }
    const items = (usersData as PaginatedUsers | undefined)?.items;
    (items ?? []).forEach((u: UserResult) => {
      const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
      map.set(u.id, name || u.email || u.username || `User #${u.id}`);
    });
    return map;
  }, [authUser?.email, authUser?.firstName, authUser?.id, authUser?.lastName, authUser?.username, isAffiliate, usersData]);

  /* ---------------- Form + state ---------------- */

  const [form, setForm] = useState<EditLeadFormState | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // Transfer state
  const [transferProductId, setTransferProductId] = useState<number | null>(
    null
  );
  const [transferToUserId, setTransferToUserId] = useState<number | null>(null);
  const [transferToProductId, setTransferToProductId] = useState<number | null>(
    null
  );

  // Keep these as plain strings (no null) to avoid select value TS issues
  const [transferReasonKey, setTransferReasonKey] = useState<string>(""); // "" = no selection
  const [transferReasonText, setTransferReasonText] = useState<string>("");

  const [visaLeadProductId, setVisaLeadProductId] = useState<number | null>(null);
  const [visaDetailsDraft, setVisaDetailsDraft] = useState<VisaDetailsDraftState>({
    countryIso2: "",
    enquiryAt: "",
    docsReceivedAt: "",
    docsSubmittedAt: "",
    docsCollectedAt: "",
    appointmentAt: "",
    invoiceAmount: "",
    receiptAmount: "",
    loginId: "",
    loginPassword: "",
    remark: "",
  });

  // Init form once lead is loaded
  useEffect(() => {
    if (!lead || initialized) return;

    const deep = lead as LeadWithProducts;

    const nonDeletedProducts = (deep.products ?? []).filter(
      (p) => !p.deletedAt
    );

    const primaryProduct = nonDeletedProducts[0] ?? null;

    const selectedProductIds = nonDeletedProducts.map((p) => p.productId);

    const initialForm: EditLeadFormState = {
      firstName: lead.firstName ?? "",
      lastName: lead.lastName ?? "",
      phone: lead.phone ?? "",
      email: lead.email ?? "",
      date: formatDateYmd(lead.leadDate),

      // Primary owner = from lead row (already aggregated)
      assignedToUserId: lead.assignedToUserId ?? null,

      status: mapStatusToUi(lead.status),
      source: mapSourceToUi(lead.source),
      priority: mapPriorityToUi(lead.priority),
      notes: lead.notes ?? "",

      products: selectedProductIds,
      productsOpen: false,

      // legacy single productId (not used by UI anymore)
      productId: primaryProduct?.productId ?? null,
    };

    setForm(initialForm);

    if (primaryProduct?.id) {
      setTransferProductId(primaryProduct.id);
      setVisaLeadProductId(primaryProduct.id);
    }

    setInitialized(true);
  }, [lead, initialized]);

  /* ---------------- Timeline ---------------- */
  const timeline: TransferTimelineEntry[] = useMemo(() => {
    if (!lead) return [];

    const deep = lead as LeadWithProducts;
    const entries: TransferTimelineEntry[] = [];

    (deep.products ?? []).forEach((p) => {
      const transfers = (p.transfers ?? []) as any[];

      transfers.forEach((t, idx: number) => {
        const when = t.createdAt ?? t.created_at ?? "";

        const fromProductId = t.fromProductId ?? t.from_product_id;
        const toProductId = t.toProductId ?? t.to_product_id;

        const fromUserId = t.fromUserId ?? t.from_user_id;
        const toUserId = t.toUserId ?? t.to_user_id;

        const fromProductName =
          (typeof fromProductId === "number" &&
            productNameById.get(fromProductId)) ||
          "—";
        const toProductName =
          (typeof toProductId === "number" &&
            productNameById.get(toProductId)) ||
          "—";

        const fromUserName =
          (typeof fromUserId === "number" &&
            userNameById.get(fromUserId)) ||
          (typeof fromUserId === "number"
            ? `User #${fromUserId}`
            : "—");

        const toUserName =
          (typeof toUserId === "number" && userNameById.get(toUserId)) ||
          (typeof toUserId === "number" ? `User #${toUserId}` : "—");

        const rawReasonText = t.reasonText ?? t.reason_text ?? "";
        const rawReasonKey = t.reasonKey ?? t.reason_key ?? "";
        const reasonFromKey =
          rawReasonKey && TRANSFER_REASON_CODE_TO_LABEL[rawReasonKey]
            ? TRANSFER_REASON_CODE_TO_LABEL[rawReasonKey]
            : rawReasonKey;

        const reason = rawReasonText || reasonFromKey || "";

        const rawActor = String(t.createdBy ?? t.created_by ?? "").trim();
        let actor = rawActor;

        // Never display raw fallback handles like "user-1".
        // Resolve to a real name when possible, otherwise hide actor line.
        const idMatch = rawActor.match(/^user-(\d+)$/i);
        if (idMatch) {
          const actorId = Number(idMatch[1]);
          if (Number.isFinite(actorId) && actorId > 0) {
            actor =
              userNameById.get(actorId) ||
              (actorId === fromUserId ? fromUserName : "") ||
              (actorId === toUserId ? toUserName : "") ||
              "";
          } else {
            actor = "";
          }
        }

        entries.push({
          id: `${p.id}-${t.id ?? idx}`,
          when,
          fromProductName,
          toProductName,
          fromUserName,
          toUserName,
          reason,
          actor,
        });
      });
    });

    entries.sort((a, b) => {
      const ta = a.when ? new Date(a.when).getTime() : 0;
      const tb = b.when ? new Date(b.when).getTime() : 0;
      return tb - ta;
    });

    return entries;
  }, [lead, productNameById, userNameById]);

  const visaProductOptions = useMemo(() => {
    const deep = lead as LeadWithProducts | undefined;
    return (deep?.products ?? [])
      .filter((p) => !p.deletedAt)
      .filter((p) => {
        const slug = productSlugById.get(p.productId);
        if (slug) return isVisaSlug(slug);
        return /visa/i.test(String(p.productName ?? ""));
      })
      .map((p) => ({
        value: p.id,
        label: p.productName ?? `Product #${p.productId}`,
      }));
  }, [lead, productSlugById]);

  const selectedVisaLeadProductId = useMemo(() => {
    if (visaLeadProductId) return visaLeadProductId;
    return visaProductOptions[0]?.value ?? null;
  }, [visaLeadProductId, visaProductOptions]);

  const selectedVisaDetails = useMemo<LeadProductVisaDetail | null>(() => {
    const deep = lead as LeadWithProducts | undefined;
    if (!deep || !selectedVisaLeadProductId) return null;
    const row = (deep.products ?? []).find(
      (p) => p.id === selectedVisaLeadProductId
    );
    return row?.visaDetails ?? null;
  }, [lead, selectedVisaLeadProductId]);

  useEffect(() => {
    setVisaDetailsDraft({
      countryIso2: selectedVisaDetails?.countryIso2 ?? "",
      enquiryAt: formatDateInput(selectedVisaDetails?.enquiryAt),
      docsReceivedAt: formatDateInput(selectedVisaDetails?.docsReceivedAt),
      docsSubmittedAt: formatDateInput(selectedVisaDetails?.docsSubmittedAt),
      docsCollectedAt: formatDateInput(selectedVisaDetails?.docsCollectedAt),
      appointmentAt: formatDateInput(selectedVisaDetails?.appointmentAt),
      invoiceAmount: formatAmountInput(selectedVisaDetails?.invoiceAmount),
      receiptAmount: formatAmountInput(selectedVisaDetails?.receiptAmount),
      loginId: selectedVisaDetails?.loginId ?? "",
      loginPassword: selectedVisaDetails?.loginPassword ?? "",
      remark: selectedVisaDetails?.remark ?? "",
    });
  }, [selectedVisaDetails, selectedVisaLeadProductId]);

  /* ---------------- Handlers ---------------- */
  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSave = useCallback(async () => {
    if (!form || invalidId || !lead) return;

    setErrorMessage(undefined);

    if (!form.products || form.products.length === 0) {
      setErrorMessage("Please select at least one product for this lead.");
      return;
    }
    if (!form.assignedToUserId) {
      setErrorMessage("Please select a lead owner to assign this lead to.");
      return;
    }

    const deep = lead as LeadWithProducts;
    const existingLines = (deep.products ?? []).filter((p) => !p.deletedAt);
    const selectedProductIds = form.products;

    // Build full-sync products[] payload
    const productsPayload: UpdateLeadBody["products"] = [];
    const visaPayload: UpsertLeadProductVisaDetailBody = {
      countryIso2: visaDetailsDraft.countryIso2.trim() || null,
      enquiryAt: visaDetailsDraft.enquiryAt || null,
      docsReceivedAt: visaDetailsDraft.docsReceivedAt || null,
      docsSubmittedAt: visaDetailsDraft.docsSubmittedAt || null,
      docsCollectedAt: visaDetailsDraft.docsCollectedAt || null,
      appointmentAt: visaDetailsDraft.appointmentAt || null,
      invoiceAmount:
        visaDetailsDraft.invoiceAmount !== ""
          ? Number(visaDetailsDraft.invoiceAmount)
          : null,
      receiptAmount:
        visaDetailsDraft.receiptAmount !== ""
          ? Number(visaDetailsDraft.receiptAmount)
          : null,
      loginId: visaDetailsDraft.loginId.trim() || null,
      loginPassword: visaDetailsDraft.loginPassword.trim() || null,
      remark: visaDetailsDraft.remark.trim() || null,
    };

    // 1) Existing lines → keep or soft-delete
    for (const line of existingLines) {
      const stillSelected = selectedProductIds.includes(line.productId);

      if (stillSelected) {
        productsPayload.push({
          id: line.id,
          productId: line.productId,
          assignedToUserId: form.assignedToUserId ?? undefined,
          status: mapStatusToApi(form.status),
          source: mapSourceToApi(form.source),
          priority: mapPriorityToApi(form.priority),
          ...(line.id === selectedVisaLeadProductId
            ? { visaDetails: visaPayload }
            : {}),
        });
      } else {
        // mark this child row as soft-deleted
        productsPayload.push({
          id: line.id,
          productId: line.productId,
          markAsDeleted: true,
        });
      }
    }

    // 2) New productIds → create new rows
    for (const productId of selectedProductIds) {
      const alreadyExists = existingLines.some(
        (line) => line.productId === productId
      );
      if (!alreadyExists) {
        productsPayload.push({
          productId,
          assignedToUserId: form.assignedToUserId ?? undefined,
          status: mapStatusToApi(form.status),
          source: mapSourceToApi(form.source),
          priority: mapPriorityToApi(form.priority),
        });
      }
    }

    const dto: UpdateLeadBody = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      leadDate: form.date,
      assignedToUserId: form.assignedToUserId!,
      status: mapStatusToApi(form.status),
      source: mapSourceToApi(form.source),
      priority: mapPriorityToApi(form.priority),
      notes: form.notes?.trim() || undefined,
      products: productsPayload,
    };

    try {
      await updateMutation.mutateAsync({ id: numericId, data: dto });
      await refetchLead();
      navigate("/leads");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to update lead.";
      setErrorMessage(msg);
    }
  }, [
    form,
    invalidId,
    lead,
    numericId,
    updateMutation,
    refetchLead,
    navigate,
    visaDetailsDraft,
  ]);

  const handleTransfer = useCallback(async () => {
    if (!lead || invalidId) return;

    setErrorMessage(undefined);

    if (!transferProductId) {
      setErrorMessage("Please select which product line you want to transfer.");
      return;
    }
    if (!transferToUserId) {
      setErrorMessage("Please select the user to transfer this lead to.");
      return;
    }

    // Normalize reasonKey for API
    const trimmedKey = transferReasonKey.trim();

    let reasonKeyForApi: string | undefined;
    if (!trimmedKey || trimmedKey === "OTHERS") {
      reasonKeyForApi = undefined;
    } else {
      reasonKeyForApi = trimmedKey;
    }

    const payload: TransferLeadProductBody = {
      toUserId: transferToUserId,
      reasonKey: reasonKeyForApi,
      reasonText: transferReasonText.trim() || undefined,
    };

    if (transferToProductId) {
      payload.toProductId = transferToProductId;
    }

    try {
      await transferMutation.mutateAsync({
        leadProductId: transferProductId,
        data: payload,
      });

      await refetchLead();
      // Reset transfer-only state for next transfer
      setTransferReasonKey("");
      setTransferReasonText("");
      setTransferToProductId(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to transfer lead.";
      setErrorMessage(msg);
    }
  }, [
    lead,
    invalidId,
    transferProductId,
    transferToUserId,
    transferToProductId,
    transferReasonKey,
    transferReasonText,
    transferMutation,
    refetchLead,
  ]);


  /* ---------------- Options for UI ---------------- */
  const STATUS_OPTIONS: LeadStatusUi[] = [
    "Pending",
    "Completed",
    "Cancelled",
  ];
  const SOURCE_OPTIONS: LeadSourceUi[] = [
    "Walk-in",
    "Referral",
    "Online",
    "Advertisement",
    "Social Media",
    "Partner",
    "Phone Inquiry",
    "Email Campaign",
    "Other",
  ];
  const PRIORITY_OPTIONS: PriorityUi[] = [
    "Low",
    "Medium",
    "High",
    "Urgent",
  ];

  return {
    // identifiers
    id,
    numericId,
    invalidId,

    // data
    lead,
    isLoading,
    isError,

    // form
    form,
    setForm,
    errorMessage,
    setErrorMessage,

    // dropdown options (React-Select style)
    productOptions,
    employeeOptions,
    STATUS_OPTIONS,
    SOURCE_OPTIONS,
    PRIORITY_OPTIONS,

    // mutations
    isSaving: updateMutation.isPending,
    isTransferring: transferMutation.isPending,
    isVisaDetailsSaving: updateMutation.isPending,

    // actions
    handleSave,
    handleCancel,

    // transfer state
    transferProductId,
    setTransferProductId,
    transferToUserId,
    setTransferToUserId,
    transferToProductId,
    setTransferToProductId,
    transferReasonKey,
    setTransferReasonKey,
    transferReasonText,
    setTransferReasonText,
    handleTransfer,

    visaLeadProductId,
    setVisaLeadProductId,
    visaProductOptions,
    visaDetailsDraft,
    setVisaDetailsDraft,

    // timeline
    timeline,
  };
}
